import {
  type FC,
  type JSX,
  type MouseEvent as ReactMouseEvent,
  type SyntheticEvent,
  useCallback,
  useState,
} from 'react';

import {
  useCardOfferDataContext,
  useCardOfferIsEarlyAccessFlow,
} from '@/microfrontends/listing/shared/context/selectors';

import { useRequestAuthentication } from '@/packages/auth/useRequestAuthentication';
import { useIsUserAuthenticatedQuery } from '@/packages/query-state/shared/store/monolith-python/cian-api/site/v1/get-user-data-by-request/query';
import { boundActionsFactory } from '@/packages/redux-state/store';

import { useListingAppContext } from '../../context/ListingAppContext';
import { useEarlyAccessFlow } from '../../hooks/card/useEarlyAccessFlow';

import { useChatTracking } from './hooks';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setActiveChatUrl: reduxActions.chat.setActiveChatUrl,
}));

type ChatButtonContainerProps = {
  chatUrl: string;
  render(props: { onClick(e: ReactMouseEvent): void; loading: boolean }): JSX.Element;
};

const ChatButtonContainer: FC<ChatButtonContainerProps> = props => {
  const [isLoading, setIsLoading] = useState(false);
  const { render, chatUrl } = props;
  const boundActions = useBoundActions();

  const { trackSendMessage, trackEarlyAccessBlocked, trackMessagePopupOpen } = useChatTracking();

  const ctx = useListingAppContext();
  const requestAuthentication = useRequestAuthentication(ctx);

  const isAuthenticated = useIsUserAuthenticatedQuery();

  const isEarlyAccessFlow = useCardOfferIsEarlyAccessFlow();

  const onMessagePopupOpen = useCallback(
    (isEarlyAccess: boolean = false) => {
      boundActions.setActiveChatUrl(chatUrl);

      trackMessagePopupOpen(isEarlyAccess);
    },
    [boundActions, chatUrl, trackMessagePopupOpen],
  );

  const handleEarlyAccessConfirmed = useCallback(() => {
    trackSendMessage(true);
    onMessagePopupOpen(true);
  }, [onMessagePopupOpen, trackSendMessage]);

  const handleEarlyAccessFlow = useEarlyAccessFlow({
    onConfirmed: handleEarlyAccessConfirmed,
    onBlocked: trackEarlyAccessBlocked,
  });

  const handleClick = useCallback(
    async (e: SyntheticEvent) => {
      e.stopPropagation();

      if (isEarlyAccessFlow) {
        setIsLoading(true);
        await handleEarlyAccessFlow();
        setIsLoading(false);

        return;
      }

      trackSendMessage();

      if (isAuthenticated) {
        onMessagePopupOpen();
      } else {
        requestAuthentication({
          onSuccess: () => onMessagePopupOpen(),
        });
      }
    },
    [
      handleEarlyAccessFlow,
      isAuthenticated,
      isEarlyAccessFlow,
      onMessagePopupOpen,
      requestAuthentication,
      trackSendMessage,
    ],
  );

  return render({
    onClick: handleClick,
    loading: isLoading,
  });
};

type ChatButtonRendererProps = Omit<ChatButtonContainerProps, 'chatUrl'>;

/**
 * @description Данный способ работы с классом нужен, что бы не вызывать множество внутренних хуков и не держать их в
 *   памяти, если у нас чатов нет вовсе
 */
export const ChatButtonRendererContainer: FC<ChatButtonRendererProps> = props => {
  const { chat } = useCardOfferDataContext();

  const chatUrl = chat?.url;

  if (!chatUrl) {
    return null;
  }

  return <ChatButtonContainer {...props} chatUrl={chatUrl} />;
};
